/**
 * `/app/me/apps` — the AI clients that can reach Boone on this person's behalf, and the lever
 * that cuts one off (issue #116, ADR 0021 decision 9).
 *
 * **There is no parameter for whose apps**, on either call. The account is the session's own,
 * decided server-side from the verified `sub`, and that absence is the authorization boundary
 * rather than an omission — the same shape as `leave_family` taking no target. The only id
 * that crosses names an **app**, and the server refuses one that is not in the caller's own
 * listing before it asks Stytch anything.
 *
 * **Every sentence here is the server's.** The question in the confirmation, what cutting off
 * achieves, and the window it cannot close all arrive composed. The last is why: the residual
 * is a measured property of the Stytch project (ADR 0021 decision 9), and a browser bundle
 * carrying its own *"eight hours"* would go on saying eight the day the project changed.
 *
 * **`logo_url` is not on the wire and this file has no field for it.** Under Dynamic Client
 * Registration it is an attacker-controllable URL, and #112's CSP on this host would refuse to
 * load it anyway — decision 9 drops it from what the surface promises rather than shipping a
 * field the page cannot render.
 */

import type { MePayloads } from "@pilotfish/api/src/app/contract.ts";
import { type AppApi, appFetch } from "@pilotfish/app-api";

/** The three sentences the confirmation dialog renders, composed server-side. */
/**
 * **The connected-apps screen, as the server answers it** — `MePayloads["GET /apps"]`, derived
 * from `me.ts` rather than described here (ADR 0025 decisions 3 and 4).
 *
 * Every sentence and every number on this screen is composed server-side, including the
 * residual window (#116, ADR 0021 decision 9): revoking deletes the Stytch grant, so the next
 * listing does not mention the app, while the access token already issued goes on verifying
 * until its own `exp`. A browser cannot compute that and must not guess at it.
 */
export type ConnectedApps = MePayloads["GET /apps"];

/** One app the caller has connected, in whichever state the server reports it. */
export type ConnectedApp = ConnectedApps["apps"][number];

/** The counts and the sentences that lead the screen. */
export type ConnectedAppsSummary = ConnectedApps["summary"];

/**
 * The wording of the confirm, composed server-side so the screen cannot soften it.
 *
 * **`NonNullable`, and the distinction is the point.** The server's *field* is nullable —
 * `confirmAll` is `null` when nothing is connected, which is what disables the button — but
 * this type names *the confirmation itself*, which either exists or is not being shown. The
 * screen bridges the two with a guard beside the `disabled`, so the dialog's state can never
 * hold a null. Deriving the field's type verbatim here would push that null into the dialog
 * and ask it to answer for a case its own gate has already refused.
 */
export type CutOffConfirmation = NonNullable<ConnectedAppsSummary["confirmAll"]>;

export function fetchConnectedApps(api: AppApi): Promise<ConnectedApps> {
  return appFetch<ConnectedApps>(api, "/app/me/apps");
}

/** What a completed cut-off-everything answers with. */
/** What the sweep answers with — `MePayloads["POST /apps/revoke-all"]`, derived. */
export type CutOffAll = MePayloads["POST /apps/revoke-all"];

export function cutOffAllApps(api: AppApi): Promise<CutOffAll> {
  return appFetch<CutOffAll>(api, "/app/me/apps/revoke-all", { method: "POST", body: {} });
}
